/**
 * @fileoverview Vue Router configuration with auth guards
 * @module router
 *
 * Includes accessibility focus management for route changes (WCAG 2.1 AA)
 */
import { createRouter, createWebHistory } from 'vue-router'
import { nextTick } from 'vue'
import { routes } from './routes'
import {
  subdomainGuard,
  authGuard,
  titleGuard,
  tenantGuard,
  welcomeGuard
} from './guards'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0 }
  }
})

// Global before guards
router.beforeEach(subdomainGuard) // Validates subdomain before auth
router.beforeEach(authGuard)
router.beforeEach(tenantGuard)
router.beforeEach(welcomeGuard)

// Global after guards
router.afterEach(titleGuard)

/**
 * Focus management for accessibility (WCAG 2.1 AA)
 *
 * After each route change, focus is moved to the main content area
 * to help screen reader users orient themselves on the new page.
 * Uses nextTick to ensure DOM is updated before focus management.
 */
router.afterEach(() => {
  nextTick(() => {
    // Look for main content landmark first (most semantic)
    const mainElement = document.querySelector('main')
    if (mainElement) {
      // Ensure main is focusable without being in tab order
      if (!mainElement.hasAttribute('tabindex')) {
        mainElement.setAttribute('tabindex', '-1')
      }
      mainElement.focus({ preventScroll: true })
      return
    }

    // Fallback to element with id="main-content" or role="main"
    const mainContent =
      document.getElementById('main-content') ||
      document.querySelector('[role="main"]')
    if (mainContent) {
      if (!mainContent.hasAttribute('tabindex')) {
        mainContent.setAttribute('tabindex', '-1')
      }
      ;(mainContent).focus({ preventScroll: true })
      return
    }

    // Last resort: focus the skip link target or document body
    const skipTarget = document.getElementById('skip-target')
    if (skipTarget) {
      if (!skipTarget.hasAttribute('tabindex')) {
        skipTarget.setAttribute('tabindex', '-1')
      }
      ;(skipTarget).focus({ preventScroll: true })
    }
  })
})

export default router
